/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type TFunction } from 'i18next';
import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { ProgressBar } from '../../components/ui/progress-bar.js';
import {
  type GoalStatus,
  type PlanGoal,
  type PlanStep,
  type PlanStepItem,
} from '../../lib/api/itp.js';
import { useGoalHistory } from '../../lib/queries/itp.js';
import { formatIst } from '../../lib/utils/datetime.js';

/** Toggle a leaf teaching step (BRD M01 #3). */
export type ToggleItem = (goalId: string, stepId: string, itemId: string, done: boolean) => void;

const GOAL_STATUSES: readonly GoalStatus[] = [
  'not_started',
  'emerging',
  'in_progress',
  'achieved',
  'maintenance',
  'discontinued',
];

interface StepLeafProps {
  readonly goalId: string;
  readonly stepId: string;
  readonly item: PlanStepItem;
  readonly onToggle: ToggleItem;
  readonly disabled: boolean;
}

/** A leaf teaching step (tier 3) — checkbox nested under its sub-target. */
function StepLeaf({ goalId, stepId, item, onToggle, disabled }: StepLeafProps): JSX.Element {
  const handle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onToggle(goalId, stepId, item.id, event.target.checked);
    },
    [onToggle, goalId, stepId, item.id],
  );
  return (
    <label className="flex items-center gap-2 text-xs text-slate-500">
      <input
        type="checkbox"
        checked={item.done}
        disabled={disabled}
        onChange={handle}
        className="focus-ring h-3.5 w-3.5 rounded border-slate-300"
      />
      <span className={item.done ? 'text-slate-400 line-through' : ''}>{item.title}</span>
    </label>
  );
}

interface StepItemProps {
  readonly goalId: string;
  readonly step: PlanStep;
  readonly onToggle: (goalId: string, stepId: string, done: boolean) => void;
  readonly onToggleItem: ToggleItem;
  readonly disabled: boolean;
}

/** A sub-target (tier 2) checkbox with its nested teaching steps (tier 3). */
function StepItem({ goalId, step, onToggle, onToggleItem, disabled }: StepItemProps): JSX.Element {
  const handle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onToggle(goalId, step.id, event.target.checked);
    },
    [onToggle, goalId, step.id],
  );
  return (
    <div>
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input
          type="checkbox"
          checked={step.done}
          disabled={disabled}
          onChange={handle}
          className="focus-ring h-4 w-4 rounded border-slate-300"
        />
        <span className={step.done ? 'text-slate-400 line-through' : ''}>{step.title}</span>
      </label>
      {step.items.length > 0 && (
        <div className="mt-1 space-y-1 pl-6">
          {step.items.map((item) => (
            <StepLeaf
              key={item.id}
              goalId={goalId}
              stepId={step.id}
              item={item}
              onToggle={onToggleItem}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface MetaRow {
  readonly label: string;
  readonly value: string;
}

/** The "Type — detail" measurement string, or '' when neither is set. */
function measurementLabel(goal: PlanGoal, t: TFunction): string {
  const typed = goal.measurementType !== null ? t(`measurementType.${goal.measurementType}`) : null;
  return [typed, goal.measurementMethod]
    .filter((part): part is string => part !== null && part !== '')
    .join(' — ');
}

/** The measurement / baseline / target rows to render under a goal. */
function metaRows(goal: PlanGoal, t: TFunction): readonly MetaRow[] {
  const measurement = measurementLabel(goal, t);
  return [
    measurement !== '' ? { label: t('therapist.planForm.measure'), value: measurement } : null,
    goal.baseline !== null
      ? { label: t('therapist.planForm.baseline'), value: goal.baseline }
      : null,
    goal.target !== null ? { label: t('therapist.planForm.target'), value: goal.target } : null,
  ].filter((row): row is MetaRow => row !== null);
}

/** Domain badge + typed-measurement / baseline / target / per-goal review (BRD M01 #1/#4). */
function GoalMeta({ goal }: { readonly goal: PlanGoal }): JSX.Element | null {
  const { t } = useTranslation();
  const domainLabel = goal.domain !== null ? t(`therapist.planForm.domains.${goal.domain}`) : null;
  const rows = metaRows(goal, t);
  if (domainLabel === null && rows.length === 0 && goal.reviewDate === null) {
    return null;
  }
  return (
    <div className="mt-2 space-y-1 text-xs text-slate-500">
      {domainLabel !== null && (
        <span className="inline-block rounded-md bg-indigo-50 px-2 py-0.5 font-medium text-indigo-600">
          {domainLabel}
        </span>
      )}
      {rows.map((row) => (
        <p key={row.label}>
          <span className="font-medium text-slate-600">{row.label}:</span> {row.value}
        </p>
      ))}
      {goal.reviewDate !== null && <p>{t('therapist.plan.reviewBy', { date: goal.reviewDate })}</p>}
    </div>
  );
}

/** Lazy, append-only status-change log for a goal (BRD M01 #6). */
function GoalStatusHistory({
  planId,
  goalId,
  open,
}: {
  readonly planId: string;
  readonly goalId: string;
  readonly open: boolean;
}): JSX.Element | null {
  const { t } = useTranslation();
  const history = useGoalHistory(planId, goalId, open);
  if (!open) {
    return null;
  }
  const entries = history.data ?? [];
  return (
    <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
      {entries.length === 0 ? (
        <p className="text-xs text-slate-400">{t('therapist.plan.noHistory')}</p>
      ) : (
        entries.map((entry) => (
          <div key={entry.id} className="text-xs text-slate-500">
            <span className="font-medium text-slate-600">
              {entry.fromStatus === null
                ? t('therapist.plan.historyInitial', { to: t(`goalStatus.${entry.toStatus}`) })
                : t('therapist.plan.historyEntry', {
                    from: t(`goalStatus.${entry.fromStatus}`),
                    to: t(`goalStatus.${entry.toStatus}`),
                  })}
            </span>
            <span className="ml-2 text-slate-400">
              {formatIst(entry.changedAt, 'd MMM yyyy, HH:mm')}
            </span>
            {entry.note !== null && entry.note !== '' && (
              <p className="text-slate-500">{entry.note}</p>
            )}
          </div>
        ))
      )}
    </div>
  );
}

interface GoalDetailRowProps {
  readonly planId: string;
  readonly goal: PlanGoal;
  readonly onUpdate: (goalId: string, status: GoalStatus, note: string | undefined) => void;
  readonly onToggleStep: (goalId: string, stepId: string, done: boolean) => void;
  readonly onToggleItem: ToggleItem;
  readonly disabled: boolean;
}

/** One goal on the plan-detail page — status + note, measurement meta, steps, history. */
export function GoalDetailRow({
  planId,
  goal,
  onUpdate,
  onToggleStep,
  onToggleItem,
  disabled,
}: GoalDetailRowProps): JSX.Element {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const onNote = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  }, []);
  const onStatus = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const trimmed = note.trim();
      onUpdate(goal.id, event.target.value as GoalStatus, trimmed === '' ? undefined : trimmed);
      setNote('');
    },
    [onUpdate, goal.id, note],
  );
  const toggleHistory = useCallback(() => {
    setHistoryOpen((value) => !value);
  }, []);
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-[12rem] flex-1">
          <p className="font-medium text-slate-800">{goal.title}</p>
          <GoalMeta goal={goal} />
          <div className="mt-2 max-w-xs">
            <ProgressBar value={goal.progressPct} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <span className="sr-only">{t('therapist.plan.updateStatus')}</span>
          <select
            className="focus-ring rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm disabled:opacity-60"
            value={goal.status}
            disabled={disabled}
            onChange={onStatus}
          >
            {GOAL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(`goalStatus.${status}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <input
        type="text"
        value={note}
        onChange={onNote}
        disabled={disabled}
        placeholder={t('therapist.plan.statusNotePlaceholder')}
        aria-label={t('therapist.plan.statusNote')}
        className="focus-ring mt-3 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
      />
      {goal.approachTags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {goal.approachTags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
      {goal.steps.length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            {t('therapist.plan.subTargets')}
          </p>
          {goal.steps.map((step) => (
            <StepItem
              key={step.id}
              goalId={goal.id}
              step={step}
              onToggle={onToggleStep}
              onToggleItem={onToggleItem}
              disabled={disabled}
            />
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={toggleHistory}
        className="focus-ring mt-3 text-xs font-medium text-indigo-600 hover:text-indigo-700"
      >
        {historyOpen ? t('therapist.plan.hideHistory') : t('therapist.plan.showHistory')}
      </button>
      <GoalStatusHistory planId={planId} goalId={goal.id} open={historyOpen} />
    </div>
  );
}
